import { CurrencyField } from '@src/clients/financialSettings/fields/CurrencyFields';
import {
  AmountValidator,
  MandatoryAmountValidator,
} from '@src/common/fields/AmountValidator';
import type { ReviewableOrder } from '@src/orders/domain/ReviewableOrder';
import { BooleanField, StringField } from '@src/permitField/PermitFields';
import type { FieldValues } from 'react-hook-form/dist/types/fields';
import type { NumberSchema } from 'yup';
import * as yup from 'yup';

export abstract class PermitFeeField {
  public static fieldName: 'jurisdictionFee' | 'serviceFee';
  public static validator = AmountValidator;
}

export class PermitJurisdictionFeeField extends PermitFeeField {
  public static fieldName = 'jurisdictionFee' as const;
}

export class PermitServiceFeeField extends PermitFeeField {
  public static fieldName = 'serviceFee' as const;
}

export class PermitInvoiceNumberField extends StringField {
  public static fieldName = 'invoiceNumber' as const;
}

export class IsWaivedField extends BooleanField {
  public static fieldName = 'isWaived' as const;
}

export interface PermitFees {
  [PermitJurisdictionFeeField.fieldName]: {
    amount: string;
    isWaived: boolean;
  };
  [PermitServiceFeeField.fieldName]: {
    amount: string;
    isWaived: boolean;
    [CurrencyField.fieldName]: string;
  };
  [PermitInvoiceNumberField.fieldName]?: string;
}

export class AmountField {
  public static fieldName = 'amount' as const;
  public static validator: NumberSchema = MandatoryAmountValidator;
}

export class TaxableField extends BooleanField {
  public static fieldName = 'taxable' as const;
}

export class DescriptionField extends StringField {
  public static fieldName = 'description' as const;
}

export interface AdditionalFee {
  id: string;
  [AmountField.fieldName]: number;
  [DescriptionField.fieldName]: string;
  [CurrencyField.fieldName]: string;
  [TaxableField.fieldName]: boolean;
}

export const AddFeeSchema = yup.object().shape({
  [AmountField.fieldName]: AmountField.validator.required(),
  [DescriptionField.fieldName]: DescriptionField.validator.required(),
  [CurrencyField.fieldName]: CurrencyField.validator.required(),
  [TaxableField.fieldName]: TaxableField.validator.required(),
});

export type FormPermitFees = {
  [permitId: string]: PermitFees;
};

export interface FeesFormFields extends FieldValues {
  permitFees: FormPermitFees;
  additionalFees: AdditionalFee[];
}

const PermitFieldValidation = yup.object().shape({
  [PermitJurisdictionFeeField.fieldName]: yup.object().shape({
    amount: PermitJurisdictionFeeField.validator,
    [IsWaivedField.fieldName]: IsWaivedField.validator,
  }),
  [PermitServiceFeeField.fieldName]: yup.object().shape({
    amount: PermitServiceFeeField.validator,
    [IsWaivedField.fieldName]: IsWaivedField.validator,
    [CurrencyField.fieldName]: CurrencyField.validator,
  }),
});

export const ReviewOrderFieldsYupValidation = (order: ReviewableOrder) =>
  yup.object().shape({
    permitFees: yup.object().shape(
      order.permitApplicationGroups.reduce((acc, group) => {
        const { id } = group.originalPermit;
        acc = {
          ...acc,
          [id]: PermitFieldValidation,
        };
        group.subsequentPermits.forEach((permit) => {
          acc = {
            ...acc,
            [permit.id]: PermitFieldValidation,
          };
        });
        return acc;
      }, {}),
    ),
    additionalFees: yup
      .array()
      .of(
        yup.object().shape({
          id: yup.string().required(),
          [AmountField.fieldName]: yup.number().required(),
          [DescriptionField.fieldName]: yup.string().trim().required(),
          [CurrencyField.fieldName]: yup.string().trim().required(),
          [TaxableField.fieldName]: yup.boolean().required(),
        }),
      )
      .required(),
  });

export const ReviewOrderFieldsDefaultValues = (
  order: ReviewableOrder,
): FeesFormFields => ({
  permitFees:
    order.permitApplicationGroups.reduce((acc, group) => {
      const { id, fees } = group.originalPermit;
      acc = {
        ...acc,
        [id]: fees,
      };
      group.subsequentPermits.forEach((subsequent) => {
        acc = {
          ...acc,
          [subsequent.id]: subsequent.fees,
        };
      });

      return acc;
    }, {}) ?? {},
  additionalFees: order.additionalFees,
});
